import { CSSProperties } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import { Typography } from '@design-system/components/typography/typography';
import auth from '@client/components/auth';
import { PrimaryButton } from '@client/new/components/button';
import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import { Async } from '@client/new/helpers/types';
import { PortalUser } from '../model/PortalUserList.types';
import { PortalUserListVM } from '../view-model/PortalUserList.viewModel';
import { PortalUserRowVM } from '../view-model/PortalUserList.viewModel.mappers';
import { PortalUserFormModal } from './components/PortalUserFormModal';
import { PortalUserListItem } from './components/PortalUserListItem';
import { SearchBar } from './components/SearchBar';
import * as styles from './PortalUserList.view.css';

const ROW_HEIGHT = 64;
const LIST_HEIGHT = 600;

type RenderRowProps = {
  rows: PortalUserRowVM[];
  onToggle: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
};

const renderRow =
  ({ rows, onToggle, onEdit, onDelete }: RenderRowProps) =>
  ({ index, style }: ListChildComponentProps) => (
    <PortalUserListItem
      row={rows[index]}
      style={style as CSSProperties}
      onToggle={onToggle}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  );

const TableHeader = () => (
  <div className={styles.tableHeader}>
    <span className={styles.headerCell}>
      <Typography variant="span" size="medium" fontWeight="medium">
        Name
      </Typography>
    </span>
    <span className={styles.headerCell}>
      <Typography variant="span" size="medium" fontWeight="medium">
        Role
      </Typography>
    </span>
    <span className={styles.headerCell}>
      <Typography variant="span" size="medium" fontWeight="medium">
        Status
      </Typography>
    </span>
    <span className={styles.headerCell}>
      <Typography variant="span" size="medium" fontWeight="medium">
        Linked creator
      </Typography>
    </span>
    <span className={styles.headerCell} />
  </div>
);

const TableBody = ({
  tableState,
  vm,
}: {
  tableState: Async<{ filteredSorted: PortalUser[] }>;
  vm: PortalUserListVM;
}) => {
  if (tableState.type === 'fetching' || tableState.type === 'not-fetched') {
    return <div className={styles.loadingState}>Loading users…</div>;
  }
  if (tableState.type === 'fetch-error') {
    return <div className={styles.errorState}>{tableState.value}</div>;
  }

  const rows = tableState.value.filteredSorted.map(vm.toRowVM);

  if (rows.length === 0) {
    return (
      <div className={styles.emptyState}>No users match these filters.</div>
    );
  }

  const onToggle = vm.lazyDispatch('TogglePortalUser');
  const onEdit = vm.lazyDispatch('OpenEdit');
  const onDelete = vm.lazyDispatch('DeletePortalUser');

  return (
    <div className={styles.listContainer}>
      <FixedSizeList
        height={Math.min(LIST_HEIGHT, rows.length * ROW_HEIGHT + 16)}
        itemCount={rows.length}
        itemSize={ROW_HEIGHT}
        width="100%"
      >
        {renderRow({ rows, onToggle, onEdit, onDelete })}
      </FixedSizeList>
    </div>
  );
};

const summaryText = (
  tableState: Async<{ totalCount: number; visibleCount: number }>,
): string => {
  if (tableState.type === 'fetched') {
    return `Showing ${tableState.value.visibleCount} of ${tableState.value.totalCount}`;
  }
  if (tableState.type === 'fetch-error') return 'Could not load portal users';
  return 'Loading portal users…';
};

const PortalUserListView = (vm: PortalUserListVM) => {
  const canCreate = auth.isAdministrator();

  return (
    <div className={styles.page}>
      <div className={styles.stickyTop}>
        <div className={styles.titleRow}>
          <div className={styles.titleBlock}>
            <Typography variant="headline" size="large">
              Portal Users
            </Typography>
            <Typography variant="span" size="small" color="N-60">
              {summaryText(vm.tableState)}
            </Typography>
          </div>
          {canCreate ? (
            <PrimaryButton onClick={vm.lazyDispatch('OpenCreate')}>
              Create new User
            </PrimaryButton>
          ) : null}
        </div>

        <div className={styles.filtersRow}>
          <SearchBar
            search={vm.search}
            role={vm.role}
            status={vm.status}
            linked={vm.linked}
            sort={vm.sort}
            onSearchChange={vm.lazyDispatch('SetSearch')}
            onRoleChange={vm.lazyDispatch('SetRole')}
            onStatusChange={vm.lazyDispatch('SetStatus')}
            onLinkedChange={vm.lazyDispatch('SetLinked')}
            onSortChange={vm.lazyDispatch('SetSort')}
          />
        </div>
      </div>

      <div className={styles.table}>
        <div className={styles.tableScrollable}>
          <TableHeader />
          <TableBody tableState={vm.tableState} vm={vm} />
        </div>
      </div>

      {vm.editor.type === 'closed' ? null : (
        <PortalUserFormModal
          key={vm.editor.type === 'edit' ? vm.editor.user.id : 'new'}
          user={vm.editor.type === 'edit' ? vm.editor.user : null}
          onClose={vm.lazyDispatch('CloseEditor')}
          save={vm.savePortalUser}
        />
      )}
    </div>
  );
};

export const PortalUserListAsyncView = ({
  vm,
}: {
  vm: Async<PortalUserListVM>;
}) => (
  <AsyncRenderer
    data={vm}
    renderer={(value) => <PortalUserListView {...value} />}
  />
);
